import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { i18n } from '@/translation/TranslationProvider.tsx';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';
import { isNil } from 'ramda';
import { ConfigMetaDefaultLocales } from '@/eventConfig/types.ts';

type LanguageContextType = {
  locale: ConfigMetaDefaultLocales | null;
  setLocale: (locale: ConfigMetaDefaultLocales) => void;
};

const LanguageContext = createContext<LanguageContextType>({
  locale: null,
  setLocale: () => {},
});

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { eventConfig } = useEventConfig();
  const [locale, setLocale] = useState<ConfigMetaDefaultLocales | null>(null);

  const userOverrodeLocaleRef = useRef(false);

  useEffect(() => {
    const defaultLocale = eventConfig?.meta?.defaultLocale;

    if (!userOverrodeLocaleRef.current && !isNil(defaultLocale) && typeof defaultLocale === 'string') {
      i18n.activate(defaultLocale as ConfigMetaDefaultLocales);

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocale(defaultLocale as ConfigMetaDefaultLocales);
    }
  }, [eventConfig?.meta?.defaultLocale]);

  const handleSetLocale = (newLocale: ConfigMetaDefaultLocales) => {
    userOverrodeLocaleRef.current = true;
    i18n.activate(newLocale);
    setLocale(newLocale);
  };

  return <LanguageContext.Provider value={{ locale, setLocale: handleSetLocale }}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
